import React,{useState} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {useAuth} from '../context/AuthContext.jsx';
import * as api from '../lib/api.js';
import './AuthScreen.css';

const {FiArrowRight,FiCheck,FiEye,FiEyeOff,FiLoader}=FiIcons;

function AuthScreen({
  brand,
  onLoginSuccess,
  onSignupSuccess,
  onRecoveryComplete
}) {
  const {passwordRecovery,setPasswordRecovery}=useAuth();
  const [mode,setMode]=useState('login');
  const [form,setForm]=useState({
    name:'',
    email:'',
    password:'',
    newPassword:''
  });
  const [showPassword,setShowPassword]=useState(false);
  const [submitting,setSubmitting]=useState(false);
  const [message,setMessage]=useState('');
  const [error,setError]=useState('');
  const isSignup=mode==='signup';
  const isRecovery=passwordRecovery;
  const canSubmit=isRecovery
    ? form.newPassword.trim().length>=8
    : isSignup
      ? form.name.trim() && form.email.trim() && form.password.trim()
      : form.email.trim() && form.password.trim();

  const updateField=(field,value)=> {
    setForm((current)=> ({...current,[field]:value}));
  };

  const switchMode=(nextMode)=> {
    setMode(nextMode);
    setShowPassword(false);
    setMessage('');
    setError('');
  };

  const handleForgotPassword=async ()=> {
    if (!form.email.trim()) {
      setError('Enter your email address first.');
      return;
    }

    setSubmitting(true);
    setError('');
    setMessage('');

    const {error:resetError}=await api.resetPasswordForEmail(form.email.trim());

    setSubmitting(false);

    if (resetError) {
      setError(resetError.message);
      return;
    }

    setMessage('Check your email for a password reset link.');
  };

  const handleMagicLink=async ()=> {
    if (!form.email.trim()) {
      setError('Enter your email address first.');
      return;
    }

    setSubmitting(true);
    setError('');
    setMessage('');

    const {error:magicLinkError}=await api.signInWithMagicLink(form.email.trim());

    setSubmitting(false);

    if (magicLinkError) {
      setError(magicLinkError.message);
      return;
    }

    setMessage('Check your email for a sign-in link.');
  };

  const handleRecoverySubmit=async (event)=> {
    event.preventDefault();

    if (!canSubmit || submitting) {
      return;
    }

    setSubmitting(true);
    setError('');
    setMessage('');

    const {error:updateError}=await api.updatePassword(form.newPassword);

    setSubmitting(false);

    if (updateError) {
      setError(updateError.message);
      return;
    }

    setPasswordRecovery(false);
    setForm((current)=> ({...current,newPassword:''}));
    setMessage('Password updated. You can continue into your workspace.');
    onRecoveryComplete?.();
  };

  const submit=async (event)=> {
    event.preventDefault();

    if (!canSubmit || submitting || isRecovery) {
      return;
    }

    setSubmitting(true);
    setError('');
    setMessage('');

    if (isSignup) {
      const {error:signUpError}=await api.signUp(
        form.email.trim(),
        form.password,
        form.name.trim()
      );

      setSubmitting(false);

      if (signUpError) {
        setError(signUpError.message);
        return;
      }

      setMessage('Check your email to confirm your account.');
      return;
    }

    const {error:signInError}=await api.signIn(
      form.email.trim(),
      form.password
    );

    setSubmitting(false);

    if (signInError) {
      setError(signInError.message);
      return;
    }

    onLoginSuccess?.();
  };

  return (
    <main
      className="auth-page"
      style={{'--brand':brand.primary,'--accent':brand.accent}}
    >
      <motion.section
        className="auth-card"
        initial={{opacity:0,y:18}}
        animate={{opacity:1,y:0}}
        transition={{duration:0.35}}
      >
        <button className="auth-logo" onClick={()=> switchMode('login')}>
          <span>D</span>
          DalePost
        </button>

        <div className="auth-heading">
          <span className="eyebrow">Creative studio</span>
          <h1>
            {isRecovery
              ? 'Choose a new password.'
              : isSignup
                ? 'Create your workspace.'
                : 'Welcome back.'}
          </h1>
          <p>
            {isRecovery
              ? 'Set a new password to finish resetting your account.'
              : isSignup
                ? 'A calmer way to create thoughtful, on-brand content.'
                : 'Pick up where your next great post begins.'}
          </p>
        </div>

        {!isRecovery && (
          <div
            className="auth-tabs"
            role="tablist"
            aria-label="Authentication mode"
          >
            <button
              className={!isSignup ? 'active' : ''}
              role="tab"
              aria-selected={!isSignup}
              onClick={()=> switchMode('login')}
            >
              Log in
            </button>
            <button
              className={isSignup ? 'active' : ''}
              role="tab"
              aria-selected={isSignup}
              onClick={()=> switchMode('signup')}
            >
              Sign up
            </button>
          </div>
        )}

        <form
          className="auth-form"
          onSubmit={isRecovery ? handleRecoverySubmit : submit}
        >
          {isSignup && (
            <label>
              Name
              <input
                type="text"
                value={form.name}
                onChange={(event)=> updateField('name',event.target.value)}
                placeholder="Your name"
                autoComplete="name"
              />
            </label>
          )}

          {!isRecovery && (
            <>
              <label>
                Email
                <input
                  type="email"
                  value={form.email}
                  onChange={(event)=> updateField('email',event.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                />
              </label>

              <label>
                Password
                <span className="password-field">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(event)=> updateField('password',event.target.value)}
                    placeholder="Enter your password"
                    autoComplete={isSignup ? 'new-password' : 'current-password'}
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={()=> setShowPassword((visible)=> !visible)}
                  >
                    <SafeIcon icon={showPassword ? FiEyeOff : FiEye} />
                  </button>
                </span>
              </label>
            </>
          )}

          {isRecovery && (
            <label>
              New password
              <span className="password-field">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={form.newPassword}
                  onChange={(event)=> updateField('newPassword',event.target.value)}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={()=> setShowPassword((visible)=> !visible)}
                >
                  <SafeIcon icon={showPassword ? FiEyeOff : FiEye} />
                </button>
              </span>
            </label>
          )}

          {!isSignup && !isRecovery && (
            <button
              type="button"
              className="forgot-link"
              onClick={handleForgotPassword}
              disabled={submitting}
            >
              Forgot password?
            </button>
          )}

          <button
            className="auth-submit"
            type="submit"
            disabled={!canSubmit || submitting}
            style={{background:brand.primary}}
          >
            {submitting ? (
              <>
                <SafeIcon icon={FiLoader} className="auth-spinner" />
                {isRecovery
                  ? 'Updating password'
                  : isSignup
                    ? 'Creating account'
                    : 'Opening workspace'}
              </>
            ) : (
              <>
                {isRecovery
                  ? 'Update password'
                  : isSignup
                    ? 'Create account'
                    : 'Log in'}
                <SafeIcon icon={FiArrowRight} />
              </>
            )}
          </button>
        </form>

        {!isSignup && !isRecovery && (
          <button
            type="button"
            className="auth-magic-link"
            onClick={handleMagicLink}
            disabled={submitting}
          >
            Email me a magic link instead
          </button>
        )}

        {!isRecovery && (
          <div className="auth-switch">
            {isSignup ? (
              <>
                <span>Already have an account?</span>
                <button onClick={()=> switchMode('login')}>Log in</button>
              </>
            ) : (
              <>
                <span>New here?</span>
                <button onClick={()=> switchMode('signup')}>
                  Create an account
                </button>
              </>
            )}
          </div>
        )}

        {error && <p className="auth-feedback auth-feedback-error">{error}</p>}
        {message && (
          <p className="auth-feedback auth-feedback-success">
            <SafeIcon icon={FiCheck} />
            {message}
          </p>
        )}
      </motion.section>
    </main>
  );
}

export default AuthScreen;
